import { ConstrainMode, DetailsListLayoutMode, DetailsRow, IColumn, IDetailsListProps, IStackItemStyles, Icon, IconButton, SelectionMode, ShimmeredDetailsList, Stack, TooltipHost } from "@fluentui/react";
import React from "react";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { useSelector } from "react-redux";
import { RootState } from "../../src/taskpane/components/_v2/redux/storeMain";
import './ContentHome.css';
import { FolderTypeIconQuery } from "../../src/taskpane/components/helpers/folderTypeIconQuery";
import { getFileTypeIconProps } from "@fluentui/react-file-type-icons";
import { iconDisplayStyle } from "../../src/taskpane/styles/iconStyles";
import { FileTypeEnum } from "../../src/taskpane/components/enums/fileTypeEnum";
import { detailListStyles, detailsListClassNames } from "../Body/ContentBodyFilesFolders";
import FileOptionPanel from "../../src/taskpane/components/panels/FileOptionPanel";
import { initialFileOptionPanelInfoSetting } from "../../src/taskpane/config/constants";
import { IFilePanelInfo } from "../../src/taskpane/interfaces/IFilePanelInfo";
import { HomeItemType } from "./ContentManagerHome";
import { IStoreItem } from "../../src/taskpane/interfaces/IStoredItem";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc"; // import plugin
import { logger } from "../../utilities/logger";

dayjs.extend(utc);

const gridFileNameStackItemStyles: IStackItemStyles = {
    root: {
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "nowrap"
    }
}

const gridOptionButtonStackItemStyles: IStackItemStyles = {
    root: {
        height: "100%",
        marginTop: -11,
        marginBottom: -11
    }
}

interface ContentHomeSharedProps {
    selectedHomeFilterType: HomeItemType
}

function copyAndSort<T>(items: T[], columnKey: string, isSortedDescending?: boolean): T[] {
    try {
        const key = columnKey as keyof T;
        return items.slice(0).sort((a: T, b: T) => {
            return ((isSortedDescending ? a[key] > b[key] : a[key] < b[key]) ? 1 : -1)
        });
    } catch (error) {
        console.error("copyAndSort: ", error);
        return items;
    }
};

export const ContentHomeShared = ({ selectedHomeFilterType }: ContentHomeSharedProps) => {
    const languageStrings = React.useContext(LanguageContext);

    const shared = useSelector((state: RootState) => state.shared);

    const [fileOptionPanelInfo, setFileOptionPanelInfo] = React.useState<IFilePanelInfo>(initialFileOptionPanelInfoSetting);
    const [items, setItems] = React.useState<IStoreItem[]>([]);

    const [columns, setColumns] = React.useState<IColumn[]>();
    const columnClickedRef = React.useRef<{ key: string, isSorted: boolean, isSortedDescending: boolean }>();

    React.useEffect(() => {
        if (shared && shared.sharedItems && selectedHomeFilterType) {
            if (selectedHomeFilterType.extension) {
                setItems(shared.sharedItems.filter(x => x.extension === selectedHomeFilterType.extension));
            } else if (selectedHomeFilterType.folderTypeIcon) {
                setItems(shared.sharedItems.filter(x => x.folderTypeIcon === selectedHomeFilterType.folderTypeIcon));
            } else {
                setItems(shared.sharedItems || [])
            }
        } else {
            setItems([]);
        }
    }, [shared, selectedHomeFilterType]);

    const _onColumnClick = React.useCallback((_ev: React.MouseEvent<HTMLElement>, column: IColumn) => {
        try {
            setColumns(prevCols => {
                const newColumns = prevCols.slice();
                const currColumn: IColumn = newColumns.filter(currCol => column.key === currCol.key)[0];

                newColumns.forEach((newCol: IColumn) => {
                    if (newCol === currColumn) {
                        currColumn.isSortedDescending = !currColumn.isSortedDescending;
                        currColumn.isSorted = true;

                        columnClickedRef.current = {
                            key: currColumn.key,
                            isSorted: currColumn.isSorted,
                            isSortedDescending: currColumn.isSortedDescending
                        };
                    } else {
                        newCol.isSorted = false;
                        newCol.isSortedDescending = true;
                    }
                });

                setItems(prevFiltered => {
                    return copyAndSort(prevFiltered, currColumn.fieldName!, currColumn.isSortedDescending);
                });

                return newColumns;
            });
        } catch (error) {
            logger(error, "ContentHomeShared (onColumnClick)");
        }
    }, [items, columns]);

    React.useEffect(() => {
        setColumns([
            {
                key: "icon",
                name: "",
                minWidth: 30,
                maxWidth: 30,
                isResizable: true,
                onRender(item) {
                    const folderIcon = FolderTypeIconQuery.getImage(item.folderTypeIcon);
                    if (item.fileType === FileTypeEnum.folder) {
                        return <TooltipHost content={item.filePath}>
                            <Icon {...getFileTypeIconProps({ type: folderIcon.fileIconType, size: 20, imageFileType: "svg" })} styles={iconDisplayStyle} />
                        </TooltipHost>
                    } else {
                        return <TooltipHost content={item.filePath}>
                            <Icon
                                {...getFileTypeIconProps({
                                    extension: item.extension,
                                    size: 20,
                                    imageFileType: "svg"
                                })}
                                styles={iconDisplayStyle}
                            />
                        </TooltipHost>
                    }
                },
            },
            {
                key: "name",
                name: languageStrings.Name,
                fieldName: "name",
                minWidth: 150,
                maxWidth: 400,
                isRowHeader: true,
                isResizable: true,
                onRender(item) {
                    return (
                        <Stack horizontal verticalAlign="center">
                            <Stack.Item grow styles={gridFileNameStackItemStyles}>
                                {item.name}
                            </Stack.Item>
                            <Stack.Item styles={gridOptionButtonStackItemStyles}>
                                <TooltipHost content={languageStrings.MoreActions}>
                                    <IconButton
                                        aria-label="Options"
                                        className={detailsListClassNames.optionButton}
                                        iconProps={{ iconName: 'More' }}
                                        onClick={() => {
                                            const options = {
                                                driveId: item.driveId,
                                                fileType: item.extension,
                                                name: item.name,
                                                lastModifiedDateTime: item.lastModifiedDateTime,
                                                isOpen: true,
                                                itemId: item.itemId,
                                                folderId: item.folderId,
                                                isRecentItem: false,
                                                filePath: item.filePath,
                                                modifiedBy: item.modifiedBy,
                                                isFromHomePage: true,
                                                isFromPinnedItems: false,
                                                cannotbeRemoved: item?.cannotBeRemoved
                                            };
                                            setFileOptionPanelInfo(options);
                                        }}
                                    />
                                </TooltipHost>
                            </Stack.Item>
                        </Stack>
                    )
                },
                isSorted: columnClickedRef.current && columnClickedRef.current.key === "name" ? columnClickedRef.current.isSorted : false,
                isSortedDescending: columnClickedRef.current && columnClickedRef.current.key === "name" ? columnClickedRef.current.isSortedDescending : false,
                onColumnClick: _onColumnClick
            },
            {
                key: "modified",
                name: languageStrings.Modified,
                fieldName: "lastModifiedDateTime",
                minWidth: 150,
                maxWidth: 160,
                isRowHeader: true,
                isResizable: true,
                onRender(item: IStoreItem) {
                    return item.lastModifiedDateTime ? dayjs.utc(item.lastModifiedDateTime).local().format("L LT") : "-";
                },
                isSorted: columnClickedRef.current && columnClickedRef.current.key === "modified" ? columnClickedRef.current.isSorted : false,
                isSortedDescending: columnClickedRef.current && columnClickedRef.current.key === "modified" ? columnClickedRef.current.isSortedDescending : false,
                onColumnClick: _onColumnClick
            },
            {
                key: "modifiedBy",
                name: languageStrings.ModifiedBy,
                fieldName: "modifiedBy",
                minWidth: 150,
                maxWidth: 200,
                isRowHeader: true,
                isResizable: true,
                onRender(item, index, column) {
                    if (item.modifiedBy) {
                        return item.modifiedBy
                    }

                    return "-"
                },
                isSorted: columnClickedRef.current && columnClickedRef.current.key === "isSorted" ? columnClickedRef.current.isSorted : false,
                isSortedDescending: columnClickedRef.current && columnClickedRef.current.key === "isSorted" ? columnClickedRef.current.isSortedDescending : false,
                onColumnClick: _onColumnClick
            },
            {
                key: "shared",
                name: languageStrings.Shared,
                fieldName: "sharedDateTime",
                minWidth: 150,
                maxWidth: 160,
                isRowHeader: true,
                isResizable: true,
                onRender(item: IStoreItem) {
                    return item.sharedDateTime ? dayjs.utc(item.sharedDateTime).local().format("L LT") : "-";
                },
                isSorted: columnClickedRef.current && columnClickedRef.current.key === "shared" ? columnClickedRef.current.isSorted : false,
                isSortedDescending: columnClickedRef.current && columnClickedRef.current.key === "shared" ? columnClickedRef.current.isSortedDescending : false,
                onColumnClick: _onColumnClick
            },
            {
                key: "sharedBy",
                name: languageStrings.SharedBy,
                fieldName: "sharedByName",
                minWidth: 150,
                // maxWidth: 200,
                isRowHeader: true,
                isResizable: true,
                onRender(item: IStoreItem, index, column) {
                    return item.sharedByName ? item.sharedByName : "-";
                },
                isSorted: columnClickedRef.current && columnClickedRef.current.key === "sharedBy" ? columnClickedRef.current.isSorted : false,
                isSortedDescending: columnClickedRef.current && columnClickedRef.current.key === "sharedBy" ? columnClickedRef.current.isSortedDescending : false,
                onColumnClick: _onColumnClick
            }
        ])
    }, []);

    const defaultShimmerColumns: IColumn[] = React.useMemo(() => {
        const columns: IColumn[] = [
            { key: "0", name: "", minWidth: 16, maxWidth: 16 },
            { key: "1", name: languageStrings.Name, minWidth: 200 },
            { key: "2", name: languageStrings.Modified, minWidth: 150 },
            { key: "3", name: languageStrings.Size, minWidth: 150 },
            { key: "4", name: languageStrings.ModifiedBy, minWidth: 150 }];
        return columns;
    }, []);

    const _onRenderRow: IDetailsListProps['onRenderRow'] = rowProps => {
        return (
            <DetailsRow {...rowProps} className={detailsListClassNames.row} />
        )
    }

    const dismissFileOptionPanel = React.useCallback(() => {
        setFileOptionPanelInfo(initialFileOptionPanelInfoSetting);
    }, []);

    return (
        <>
            <ShimmeredDetailsList
                styles={detailListStyles}
                compact={false}
                onRenderRow={_onRenderRow}
                items={items}
                columns={columns?.length > 0 ? columns : defaultShimmerColumns}
                selectionMode={SelectionMode.none}
                constrainMode={ConstrainMode.unconstrained}
                setKey="multiple"
                enableShimmer={shared.isSharedItemsLoading}
                layoutMode={DetailsListLayoutMode.justified}
                ariaLabelForSelectionColumn="Toggle selection"
                ariaLabelForSelectAllCheckbox="Toggle selection for all items"
                checkButtonAriaLabel="Row checkbox"
                className="xb-search-grid"
            />
            {fileOptionPanelInfo && <FileOptionPanel info={fileOptionPanelInfo} dismissPanel={dismissFileOptionPanel} />}
        </>
    )
};